/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { parseISO } from 'date-fns';

import { DateInputComp } from 'components/Views/components/DateInputComp';

import { convertDateToString6 } from 'constants/helper';

import { DatePicker } from 'uiToolkitV1/DatePicker';
import { DateRangeSelectDashed } from 'uiToolkitV1/DateRange/DateRangeSelectDashed';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { isMonthRangeDOJ, MONTH_OPTIONS, MONTH_RANGE } from './dojUtils';

export const DOJ_OPTIONS = [
  {
    label: 'Before',
    value: 'BEFORE',
  },
  {
    label: 'Between',
    value: 'BETWEEN',
  },
  {
    label: 'After',
    value: 'AFTER',
  },
];

const DateRangeSelector = ({ employeeFilterDOJData, handleChange }) => {
  const { type, endDate, startDate } = employeeFilterDOJData?.[0] ?? {};
  const startDateObj = useMemo(() => (startDate ? new Date(parseISO(startDate)) : null), [startDate]);
  const endDateObj = useMemo(() => (endDate ? new Date(parseISO(endDate)) : null), [endDate]);

  const onDateRangeChange = useCallback(
    (range) => {
      handleChange([
        {
          type,
          startDate: convertDateToString6(range.startDate),
          endDate: convertDateToString6(range.endDate),
        },
      ]);
    },
    [handleChange, type]
  );

  return (
    <DateRangeSelectDashed
      onSelect={onDateRangeChange}
      placeholderText="Select Range"
      initialDateRange={{ startDate: startDateObj, endDate: endDateObj }}
      // onClear={onDateRangeClear}
      // minDate={minDate}
      // maxDate={maxDate}
      displayCancelButton={false}
      displayClearButton={false}
      width="80%"
      isPortalContent={false}
    />
  );
};

const SingleDateSelector = ({ employeeFilterDOJData, handleChange }) => {
  const { type: currType, endDate, startDate } = employeeFilterDOJData?.[0] ?? {};
  const type = currType || DOJ_OPTIONS[0].value;

  const selectedDate = type === 'BEFORE' ? endDate : startDate;

  const onChange = (value) => {
    handleChange([
      {
        type,
        endDate: type === 'BEFORE' ? convertDateToString6(value) : null,
        startDate: type === 'AFTER' ? convertDateToString6(value) : null,
      },
    ]);
  };

  const selectedDateObj = useMemo(() => (selectedDate ? parseISO(selectedDate) : null), [selectedDate]);
  return (
    <DatePicker
      CustomInput={DateInputComp}
      selectedDate={selectedDateObj}
      onChange={(val) => onChange(val)}
      customInputProps={{ width: '160px' }}
      // minDate={minEndDate}
      // maxDate={maxEndDate}
      // popperPlacement="bottom"
    />
  );
};

const MonthSelect = ({ value, onChange, minMonth = 1 }) => (
  <Box w="120px">
    <Select value={value ? String(value) : undefined} onValueChange={(val) => onChange(Number(val))}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Month" />
      </SelectTrigger>
      {/* Fit the 12-month list to the space left on screen and scroll, so months aren't cut off on short windows */}
      <SelectContent style={{ zIndex: 1510, maxHeight: 'var(--radix-select-content-available-height)' }}>
        {MONTH_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={String(option.value)} disabled={option.value < minMonth}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Box>
);

// Months only, no year. End months before the start month are disabled, so the range can't wrap into the next year.
const MonthRangeSelector = ({ employeeFilterDOJData, handleChange }) => {
  const { startMonth, endMonth } = employeeFilterDOJData?.[0] ?? {};

  const onChange = (newStartMonth, newEndMonth) => {
    handleChange([{ type: MONTH_RANGE, startMonth: newStartMonth, endMonth: newEndMonth }]);
  };

  // Label and pickers on separate rows so the filter popup keeps its default width and isn't pushed off-screen
  return (
    <Flex flexDirection="column" gap={2}>
      <TextBodyMedium>Include employees hired from</TextBodyMedium>
      <Flex alignItems="center" gap={2}>
        <MonthSelect
          value={startMonth}
          onChange={(month) => onChange(month, !endMonth || endMonth < month ? month : endMonth)}
        />
        <TextBodyMedium>to</TextBodyMedium>
        <MonthSelect
          value={endMonth}
          minMonth={startMonth ?? 1}
          onChange={(month) => onChange(startMonth ?? month, month)}
        />
      </Flex>
    </Flex>
  );
};

const DOJ_MODE_OPTIONS = [
  { label: 'Date range', value: 'DATE_RANGE' },
  { label: 'Month range', value: MONTH_RANGE },
];

export const DOJComp = ({ employeeFilterDOJData, handleChange }) => {
  const isMonthRange = isMonthRangeDOJ(employeeFilterDOJData?.[0]);
  const type = employeeFilterDOJData?.[0]?.type ?? DOJ_OPTIONS[0].value;

  const onModeChange = (value) => {
    handleChange([
      value === MONTH_RANGE
        ? { type: MONTH_RANGE, startMonth: null, endMonth: null }
        : { type: DOJ_OPTIONS[0].value, startDate: null, endDate: null },
    ]);
  };

  const onChange = (value) => {
    handleChange([
      {
        type: value,
        startDate: null,
        endDate: null,
      },
    ]);
  };

  const modeSelect = (
    <Flex alignItems="center" gap={2} mb={3}>
      <TextBodyMedium>Filter by</TextBodyMedium>
      <Box w="150px">
        <Select value={isMonthRange ? MONTH_RANGE : 'DATE_RANGE'} onValueChange={onModeChange}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent style={{ zIndex: 1510 }}>
            {DOJ_MODE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Box>
    </Flex>
  );

  if (isMonthRange) {
    return (
      <Box p="2">
        {modeSelect}
        <MonthRangeSelector employeeFilterDOJData={employeeFilterDOJData} handleChange={handleChange} />
      </Box>
    );
  }

  return (
    <Box p="2">
      {modeSelect}
      <Flex alignItems="center" gap={2}>
        <TextBodyMedium>Include employees hired </TextBodyMedium>

        <Box w="110px">
          <Select value={type} onValueChange={onChange}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ zIndex: 1510 }}>
              {DOJ_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Box>
      </Flex>

      {type === 'BETWEEN' ? (
        <DateRangeSelector employeeFilterDOJData={employeeFilterDOJData} handleChange={handleChange} />
      ) : (
        <SingleDateSelector employeeFilterDOJData={employeeFilterDOJData} handleChange={handleChange} />
      )}
    </Box>
  );
};
